import React, { useCallback, useEffect, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";

import * as MessageList from "components/MessageList";
import * as AccountService from "services/accounts";
import * as AccountsTable from "components/Admin/Accounts/AccountsTable";
import * as AccountsTableColumns from "components/Admin/Accounts/AccountsTableColumns";
import { CustomButton } from "components/CustomButton";
import { store } from "services/state";
import _ from "lodash";
import { Divider } from "@mui/material";

const RegisteredAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [messages, sendMessage] = MessageList.useMessages();
  const [demoteModalOpen, setDemoteModalOpen] = useState(false);

  const refreshAccounts = async () => {
    const [all, admins] = await Promise.all([
      AccountService.fetchRegisteredAccounts(),
      AccountService.fetchAdminAccounts(),
    ]);

    const allMap = {};

    all.forEach((user) => {
      allMap[user.UserId] = user;
    });
    admins.forEach((admin) => {
      admin.IsAdmin = true;
      if (admin.EmailAddress === store.user.email) {
        admin.EmailAddress += " (you)";
      }
      allMap[admin.UserId] = admin;
    });

    return setAccounts(Object.values(allMap));
  };

  const isYou = (user) => {
    return _.get(store, "user.email") + " (you)" === user.EmailAddress;
  };

  // Initial load
  useEffect(() => {
    refreshAccounts().finally(() => setLoading(false));
  }, []);

  const onSelectAccount = useCallback(
    (account) => setSelectedAccount(account),
    [setSelectedAccount]
  );

  const onConfirmDelete = useCallback(async () => {
    setLoading(true);
    setDeleteModalOpen(false);
    try {
      await AccountService.deleteAccountByUserId(selectedAccount.UserId);
      sendMessage((dismiss) => (
        <DeleteSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ));
      await refreshAccounts();
    } catch (error) {
      sendMessage((dismiss) => (
        <DeleteFailureMessage
          account={selectedAccount}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccount]);

  const onConfirmPromote = useCallback(async () => {
    setLoading(true);
    setPromoteModalOpen(false);
    try {
      await AccountService.promoteAccountByUserId(selectedAccount.UserId);
      sendMessage((dismiss) => (
        <PromoteSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ));
      await refreshAccounts();
    } catch (error) {
      sendMessage((dismiss) => (
        <PromoteFailureMessage
          account={selectedAccount}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccount]);

  const onConfirmDemote = useCallback(async () => {
    setLoading(true);
    setDemoteModalOpen(false);
    try {
      await AccountService.demoteAccountByUserId(selectedAccount.UserId);
      sendMessage((dismiss) => (
        <DemoteSuccessMessage account={selectedAccount} dismiss={dismiss} />
      ));
      await refreshAccounts();
    } catch (error) {
      sendMessage((dismiss) => (
        <DemoteFailureMessage
          account={selectedAccount}
          dismiss={dismiss}
          errorMessage={error.message}
        />
      ));
    } finally {
      setLoading(false);
    }
  }, [sendMessage, selectedAccount]);

  return (
    <Container maxWidth={false} sx={{ p: 2, margin: 0, width: '100%' }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: '400' }}>
        Registered accounts
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <MessageList.MessageList messages={messages} />
      <AccountsTable.AccountsTable
        accounts={accounts}
        columns={[
          AccountsTableColumns.EmailAddress,
          AccountsTableColumns.IsAdmin,
          AccountsTableColumns.DateRegistered,
          AccountsTableColumns.RegistrationMethod,
          // AccountsTableColumns.ApiKeyId
        ]}
        loading={loading}
        selectedAccount={selectedAccount}
        onSelectAccount={onSelectAccount}
      >
        <TableActions
          canDelete={!loading && selectedAccount && !isYou(selectedAccount)}
          onClickDelete={() => setDeleteModalOpen(true)}
          canPromote={!loading && selectedAccount}
          canDemote={!loading && selectedAccount && !isYou(selectedAccount)}
          isAdmin={selectedAccount && selectedAccount.IsAdmin}
          onClickPromote={() => setPromoteModalOpen(true)}
          onClickDemote={() => setDemoteModalOpen(true)}
        />
      </AccountsTable.AccountsTable>
      <DeleteAccountModal
        account={selectedAccount}
        onConfirm={onConfirmDelete}
        open={deleteModalOpen}
        isAdmin={selectedAccount && selectedAccount.IsAdmin}
        onClose={() => setDeleteModalOpen(false)}
      />
      <PromoteAccountModal
        account={selectedAccount}
        onConfirm={onConfirmPromote}
        open={promoteModalOpen}
        onClose={() => setPromoteModalOpen(false)}
      />
      <DemoteAccountModal
        account={selectedAccount}
        onConfirm={onConfirmDemote}
        open={demoteModalOpen}
        onClose={() => setDemoteModalOpen(false)}
      />
    </Container>
  );
};
export default RegisteredAccounts;

const TableActions = React.memo(
  ({ canDelete, onClickDelete, canPromote, canDemote, onClickPromote, isAdmin, onClickDemote }) => (
    <ButtonGroup aria-label="table actions" disableElevation variant="contained">
      <CustomButton disabled={!canDelete} onClick={onClickDelete}>
        Delete
      </CustomButton>
      <CustomButton disabled={!canPromote || isAdmin} onClick={onClickPromote}>
        Promote to Admin
      </CustomButton>
      <CustomButton disabled={!canDemote || !isAdmin} onClick={onClickDemote}>
        Demote to User
      </CustomButton>
    </ButtonGroup>
  )
);

const DeleteAccountModal = React.memo(
  ({ account, onConfirm, open, onClose, isAdmin }) =>
    account && (
      <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
        <DialogTitle>Delete account</DialogTitle>
        <DialogContent dividers>
          {isAdmin && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Danger! This is an admin account.</strong>
              <div>
                Deleting an admin account could cause temporary loss of access
                and temporary inability to configure the developer portal.
              </div>
            </Alert>
          )}
          <p>
            Are you sure you want to delete the account <strong>{account.EmailAddress}</strong>, and de-activate the
            associated API key? This action is irreversible.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="error" onClick={onConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    )
);

const PromoteAccountModal = React.memo(
  ({ account, onConfirm, open, onClose }) =>
    account && (
      <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
        <DialogTitle>Confirm promotion</DialogTitle>
        <DialogContent dividers>
          <p>
            Are you sure you want to promote the account <strong>{account.EmailAddress}</strong> to Admin? This will allow
            the account to perform any Admin actions, including deleting and
            promoting other accounts.
          </p>
          <p>
            Only the owner of the Developer Portal can demote the account,
            through the Cognito console.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={onConfirm}>
            Promote
          </Button>
        </DialogActions>
      </Dialog>
    )
);

const DemoteAccountModal = React.memo(
  ({ account, onConfirm, open, onClose }) =>
    account && (
      <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
        <DialogTitle>Confirm demotion</DialogTitle>
        <DialogContent dividers>
          <p>
            Are you sure you want to demote the account <strong>{account.EmailAddress}</strong> to normal user? This will allow
            the account to deny any Admin actions, including deleting and
            promoting other accounts.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" color="error" onClick={onConfirm}>
            Demote
          </Button>
        </DialogActions>
      </Dialog>
    )
);

const DeleteSuccessMessage = React.memo(({ account, dismiss }) => (
  <Alert onClose={dismiss} severity="success">
    Deleted account <strong>{account.EmailAddress}</strong>.
  </Alert>
));

const DeleteFailureMessage = React.memo(
  ({ account, errorMessage, dismiss }) => (
    <Alert onClose={dismiss} severity="error">
      <div>
        <p>Failed to delete account <strong>{account.EmailAddress}</strong>.</p>
        {errorMessage && <p>Error message: {errorMessage}</p>}
      </div>
    </Alert>
  )
);

const PromoteSuccessMessage = React.memo(({ account, dismiss }) => (
  <Alert onClose={dismiss} severity="success">
    Promoted account <strong>{account.EmailAddress}</strong>.
  </Alert>
));

const DemoteSuccessMessage = React.memo(({ account, dismiss }) => (
  <Alert onClose={dismiss} severity="success">
    Demoted account <strong>{account.EmailAddress}</strong>.
  </Alert>
));

const PromoteFailureMessage = React.memo(
  ({ account, errorMessage, dismiss }) => (
    <Alert onClose={dismiss} severity="error">
      <div>
        <p>Failed to promote account <strong>{account.EmailAddress}</strong>.</p>
        {errorMessage && <p>Error message: {errorMessage}</p>}
      </div>
    </Alert>
  )
);

const DemoteFailureMessage = React.memo(
  ({ account, errorMessage, dismiss }) => (
    <Alert onClose={dismiss} severity="error">
      <div>
        <p>Failed to demote account <strong>{account.EmailAddress}</strong>.</p>
        {errorMessage && <p>Error message: {errorMessage}</p>}
      </div>
    </Alert>
  )
);
